import React, { useState, useEffect, useCallback } from 'react';
import './App.css';

// ─── Scenario Data ─────────────────────────────────────────────────────────────
const ITEM          = 'Ribeye Steak (Angus Choice, ~2.1 lb)';
const AUTH_AMOUNT   = '$14.99';  // static PLU pre-auth — price per lb estimate
const ACTUAL_AMOUNT = '$38.47';  // scale: 2.1 lb × $18.32/lb (market-priced)
const DELTA         = '$23.48';  // the variance that causes the decline

// Timeline (ms) per step becoming "active" — controls animation pacing
const BAD_TIMING  = [0, 1400, 3200, 5400, 7400, 9800];  // slow, painful — 6 steps
const GOOD_TIMING = [0, 1000, 2400, 3200];                // fast — 4 steps

const BAD_STEPS = [
  {
    icon: '💳',
    title: 'Red Card Declined at POS',
    desc:  `Static auth of ${AUTH_AMOUNT} rejected — actual scale price ${ACTUAL_AMOUNT}. Delta ${DELTA} exceeds card limit.`,
    badge: { label: 'DECLINED', cls: 'red' },
  },
  {
    icon: '📞',
    title: 'Dasher Calls Support',
    desc:  'Routed through IVR tree → agent queue. Avg hold: 7–11 min. Order batch timer running.',
    badge: { label: '~9 min hold', cls: 'amber' },
  },
  {
    icon: '🧑‍💻',
    title: 'Agent Manually Re-Authorizes',
    desc:  'Agent pulls order, overrides auth manually in internal tooling. Error-prone, no PLU lookup.',
    badge: { label: 'Human touch', cls: 'grey' },
  },
  {
    icon: '🚫',
    title: 'Order Cancelled — Full Customer Refund Issued',
    desc:  '100% of incidents result in full refund. Avg order value on weight-variant items est. $45–75.',
    badge: { label: '~$60 avg refund', cls: 'red' },
  },
  {
    icon: '🔄',
    title: 'Dasher Retries Checkout',
    desc:  'Re-swipes card. If batch contains other stores, those deliveries are now late. SLA blown.',
    badge: { label: 'SLA breach', cls: 'red' },
  },
  {
    icon: '💸',
    title: 'Cascading OPEX Damage',
    desc:  'Support ticket logged. Batch degraded. ~$6 inconvenience credit disbursed to Dasher — direct cash out, zero delivery completed.',
    badge: { label: '~$66 total cost', cls: 'red' },
  },
];

const GOOD_STEPS = [
  {
    icon: '💳',
    title: 'Red Card Declined at POS',
    desc:  `Same ${DELTA} variance. Static PLU auth of ${AUTH_AMOUNT} vs. scale price ${ACTUAL_AMOUNT}.`,
    badge: { label: 'DECLINED', cls: 'red' },
  },
  {
    icon: '📷',
    title: 'In-App OCR Scan (3 sec)',
    desc:  'Dasher taps "Scan Label." Camera reads barcode: 2.1 lb × $18.32 = $38.47. Parsed instantly.',
    badge: { label: '~3 sec', cls: 'green' },
  },
  {
    icon: '⚡',
    title: 'Dynamic PLU Buffer Applied',
    desc:  `API sends OCR payload. Backend applies 15% auth buffer → $44.24 authorized. Zero agents.`,
    badge: { label: 'Auto-auth', cls: 'green' },
  },
  {
    icon: '✅',
    title: 'Dasher Retries — Approved',
    desc:  'Card approved in-lane. Batch intact. No SLA breach. No ticket. No hold.',
    badge: { label: 'Zero OPEX', cls: 'green' },
  },
];

// ─── Sub-components ────────────────────────────────────────────────────────────

function ScanIcon() {
  return (
    <div className="scan-anim">
      {[1,2,3,4,5].map(i => <div key={i} className="scan-bar" />)}
    </div>
  );
}

function Ticker({ running, color, finalVal }) {
  const [ms, setMs] = useState(0);

  useEffect(() => {
    if (!running) { if (finalVal !== undefined) setMs(finalVal); return; }
    setMs(0);
    const start = Date.now();
    const id = setInterval(() => setMs(Date.now() - start), 50);
    return () => clearInterval(id);
  }, [running, finalVal]);

  const display = ms >= 60000
    ? `${(ms / 60000).toFixed(1)} min`
    : `${(ms / 1000).toFixed(1)}s`;

  return <span className={`ticker ${color}`}>{display}</span>;
}

function Step({ data, state /* 'waiting' | 'active' | 'done' */, color, isLast }) {
  const cls =
    state === 'active' ? `active-${color}` :
    state === 'done'   ? 'done'            : '';

  return (
    <div className="step-wrap visible">
      <div className={`step ${cls}`}>
        <div className="step-icon-wrap">
          {state === 'active' && color === 'green' && data.icon === '📷'
            ? <ScanIcon />
            : data.icon}
        </div>
        <div className="step-text">
          <div className="step-title">{data.title}</div>
          <div className="step-desc">{data.desc}</div>
        </div>
        {data.badge && (
          <div className={`step-badge ${state === 'waiting' ? 'grey' : data.badge.cls}`}>
            {data.badge.label}
          </div>
        )}
      </div>
      {!isLast && (
        <div className={`step-connector ${state === 'done' ? color : 'dim'}`} />
      )}
    </div>
  );
}

function Panel({ side, steps, timing, color, label, tag, done, onDone }) {
  const [active, setActive] = useState(-1);

  const run = useCallback(() => {
    setActive(-1);
    timing.forEach((t, i) => {
      setTimeout(() => {
        setActive(i);
        if (i === steps.length - 1) setTimeout(onDone, 600);
      }, t);
    });
  }, [timing, steps.length, onDone]);

  // Expose run to parent via ref-equivalent (prop callback on mount)
  useEffect(() => { run(); }, [run]);

  // elapsed ticker
  const totalDuration = timing[timing.length - 1] + 1200;
  const [elapsed, setElapsed] = useState(null);

  useEffect(() => {
    const start = Date.now();
    const id = setInterval(() => {
      const t = Date.now() - start;
      setElapsed(t);
      if (t >= totalDuration) clearInterval(id);
    }, 50);
    return () => clearInterval(id);
  }, [totalDuration]);

  const elapsedDisplay = elapsed !== null
    ? elapsed >= 60000
      ? `${(elapsed / 60000).toFixed(1)} min`
      : `${(elapsed / 1000).toFixed(1)}s`
    : '0.0s';

  const finalDisplay = color === 'red' ? '14.8 min' : '18s';

  return (
    <div className="panel">
      <div className="panel-header">
        <div className="panel-label">
          <div className={`panel-letter ${color}`}>{side}</div>
          <div className="panel-name">{label}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className={`ticker ${color}`}>
            {done ? finalDisplay : elapsedDisplay}
          </span>
          <div className={`panel-tag ${color}`}>{tag}</div>
        </div>
      </div>

      <div className="panel-body">
        {/* Order context row */}
        <div className="order-context">
          <div>
            <div style={{ fontSize: 10, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>Item</div>
            <div className="order-context-item">{ITEM}</div>
          </div>
          <div className="order-context-right">
            <div className="order-context-auth">{AUTH_AMOUNT} authorized</div>
            <div className="order-context-actual">Actual scale: {ACTUAL_AMOUNT} (+{DELTA})</div>
          </div>
        </div>

        {/* Flow steps */}
        <div className="flow">
          {steps.map((s, i) => (
            <Step
              key={i}
              data={s}
              state={active === i ? 'active' : active > i ? 'done' : 'waiting'}
              color={color}
              isLast={i === steps.length - 1}
            />
          ))}
        </div>

        {/* Outcome metrics */}
        <div className={`outcome ${color} ${done ? 'visible' : ''}`}>
          <div className="outcome-title">
            {color === 'red' ? '⚠ Outcome: Support Ticket + SLA Breach' : '✓ Outcome: Zero-Touch Resolution'}
          </div>
          <div className="outcome-metrics">
            {color === 'red' ? <>
              <div className="outcome-metric">
                <div className="outcome-metric-value">~14.8 min</div>
                <div className="outcome-metric-label">Resolution time</div>
              </div>
              <div className="outcome-metric">
                <div className="outcome-metric-value">~$66 direct cost</div>
                <div className="outcome-metric-label">Pay + refund per incident</div>
              </div>
              <div className="outcome-metric">
                <div className="outcome-metric-value">1 ticket</div>
                <div className="outcome-metric-label">Support OPEX per incident</div>
              </div>
              <div className="outcome-metric">
                <div className="outcome-metric-value">Batch blown</div>
                <div className="outcome-metric-label">Downstream SLA impact</div>
              </div>
            </> : <>
              <div className="outcome-metric">
                <div className="outcome-metric-value">~18 sec</div>
                <div className="outcome-metric-label">Resolution time</div>
              </div>
              <div className="outcome-metric">
                <div className="outcome-metric-value">$0</div>
                <div className="outcome-metric-label">Direct cash exposure</div>
              </div>
              <div className="outcome-metric">
                <div className="outcome-metric-value">$0</div>
                <div className="outcome-metric-label">Support OPEX</div>
              </div>
              <div className="outcome-metric">
                <div className="outcome-metric-value">Batch intact</div>
                <div className="outcome-metric-label">SLA preserved</div>
              </div>
            </>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── App ───────────────────────────────────────────────────────────────────────
export default function App() {
  const [key, setKey]           = useState(0);   // increment to replay
  const [badDone, setBadDone]   = useState(false);
  const [goodDone, setGoodDone] = useState(false);

  const replay = () => {
    setBadDone(false);
    setGoodDone(false);
    setKey(k => k + 1);
  };

  return (
    <div className="app">
      <div className="header">
        <div className="header-eyebrow">DoorDash · Red Card Failure Mode · Proof of Concept</div>
        <h1 className="header-title">
          <span className="red">Current State</span> vs. <span className="green">Zero-Touch Fix</span>
        </h1>
        <p className="header-subtitle">
          Market-priced items (meat, seafood) trigger weight-variance declines because the static
          PLU pre-auth can't anticipate actual scale price. Every incident costs ~15 min of support OPEX.{' '}
          <span className="subtitle-highlight">Every incident also triggers a full customer refund (avg ~$60)
          plus a partial Dasher disbursement — making this a direct cash event, not merely an OPEX inefficiency.</span>
        </p>
      </div>

      {/* Top-level delta stats */}
      <div className="delta-bar">
        <div className="delta-stat">
          <div className="delta-stat-value amber">{DELTA}</div>
          <div className="delta-stat-label">Avg price delta at scale</div>
        </div>
        <div className="delta-divider" />
        <div className="delta-stat">
          <div className="delta-stat-value red">~14.8 min</div>
          <div className="delta-stat-label">Legacy resolution time</div>
        </div>
        <div className="delta-divider" />
        <div className="delta-stat">
          <div className="delta-stat-value green">~18 sec</div>
          <div className="delta-stat-label">OCR resolution time</div>
        </div>
        <div className="delta-divider" />
        <div className="delta-stat">
          <div className="delta-stat-value green">49×</div>
          <div className="delta-stat-label">Speed improvement</div>
        </div>
        <div className="delta-divider" />
        <div className="delta-stat">
          <div className="delta-stat-value red">$8M–14M est.</div>
          <div className="delta-stat-label">Annual direct exposure (est.)</div>
        </div>
      </div>
      <div className="delta-footnote">
        * Est. based on avg $66/incident loaded cost (refund + Dasher credit). Incident volume extrapolated from personal observation rate across 5,000+ deliveries.
      </div>

      {/* A/B panels */}
      <div className="ab-grid">
        <Panel
          key={`bad-${key}`}
          side="A"
          label="Current State — Legacy Support Path"
          tag="Status Quo"
          steps={BAD_STEPS}
          timing={BAD_TIMING}
          color="red"
          done={badDone}
          onDone={() => setBadDone(true)}
        />
        <Panel
          key={`good-${key}`}
          side="B"
          label="Proposed Fix — OCR Zero-Touch Resolution"
          tag="Proposed"
          steps={GOOD_STEPS}
          timing={GOOD_TIMING}
          color="green"
          done={goodDone}
          onDone={() => setGoodDone(true)}
        />
      </div>

      <div className="replay-wrap">
        <button className="replay-btn" onClick={replay}>↺ Replay Animation</button>
      </div>
    </div>
  );
}
